import { inject, Injectable } from '@angular/core';
import { NavigationHistoryStorageService, SITE_TITLE } from '@core';
import { NativeNavigationOptions, NavigationPanelActions } from './native-bridge.model';
import '@models';
export const MAX_WAIT_TIME_MS = 5000;
export const WAIT_INTERVAL_MS = 10;

@Injectable({ providedIn: 'root' })
export class NativeBridgeService {
  private navigationHistoryService = inject(NavigationHistoryStorageService);
  private themeObserver?: MutationObserver;
  readonly nativeToThirdPartyNamespaceKey = 'NativeToThirdPartyWVChaseJSBridgeHandler';

  private get thirdPartyToNative() {
    return (window as any).ThirdPartyWVToNativeChaseJSBridgeHandler;
  }

  get isThirdPartyToNativeAvailable() {
    return !!this.thirdPartyToNative;
  }

  get isHybridApp(): boolean {
    return window.CHASE?.TagManager?.isServerSideTaggingEnabled();
  }

  get isIOS(): boolean {
    console.log('Check if device is iOS', /iPad|iPhone|iPod/.test(navigator.userAgent), navigator.userAgent);
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  setupNativeBridgeApi(): void {
    let elapsed = 0;
    const waitForBridgeAndSetup = () => {
      if (this.thirdPartyToNative) {
        console.log('JSBridge namespace found in global object, setting native title...');
        this.setNativeTitle();
        this.setupNativeBackButtonHandling();
        this.observeThemeChanges();
      } else if (elapsed < MAX_WAIT_TIME_MS) {
        elapsed += WAIT_INTERVAL_MS;
        setTimeout(waitForBridgeAndSetup, WAIT_INTERVAL_MS);
      } else if (elapsed >= MAX_WAIT_TIME_MS) {
        console.log(
          `Native bridge APIs (window.ThirdPartyWVToNativeChaseJSBridgeHandler) was not available within ${MAX_WAIT_TIME_MS / 1000} sec`,
        );
      }
    };

    console.log('Start waiting for thirdPartyToNative bridge APIs');
    waitForBridgeAndSetup();
  }

  /**
   * Closes the hybrid WebView
   */
  finish(): void {
    if (this.thirdPartyToNative?.goToNativePage) {
      console.log('jsBridge.onFinish method found, call...');
      this.thirdPartyToNative.goToNativePage({ navKey: 'chaseShoppingHub' });
    }
  }

  private setNativeTitle(): void {
    if (document.readyState === 'complete') {
      const pageTitle = document.title ?? SITE_TITLE.MOBILE;
      this.updateNavigationBar(this.buildNavigationPanelPayload(pageTitle));
    } else {
      window.addEventListener(
        'load',
        () => {
          const pageTitle = document.title ?? SITE_TITLE.MOBILE;
          this.updateNavigationBar(this.buildNavigationPanelPayload(pageTitle));
        },
        { once: true },
      );
    }
  }

  /**
   * Native paints the header title/Close color when setHeader (updateNativeNavigationBarButtons) is
   * called, and does not re-theme them afterwards. Since setHeader runs only once at startup, after an
   * in-app theme switch the bar background follows the live `color-scheme` while the title/Close keep
   * their original-theme color and become invisible. Re-push the header on every theme change so native
   * repaints them for the current appearance.
   */
  private observeThemeChanges(): void {
    if (this.themeObserver) {
      return;
    }
    this.themeObserver = new MutationObserver(() => {
      // Defer so the `data-theme`/`color-scheme` change is fully applied before native re-reads it.
      setTimeout(() => this.setNativeTitle(), 0);
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  private setupNativeBackButtonHandling(): void {
    this.populateWindowWithNativeToThirdPartyNamespace();
    this.setNativeBackButtonClickHandler();
  }

  /**
   * Sets the native shell header with title and visibility of buttons
   */
  private updateNavigationBar(options: NativeNavigationOptions): void {
    if (this.thirdPartyToNative?.updateNativeNavigationBarButtons) {
      console.log('updateNativeNavigationBarButtons method found, call...');
      this.thirdPartyToNative.updateNativeNavigationBarButtons(options);
    }
  }

  private buildNavigationPanelPayload(pageTitle: string): NativeNavigationOptions {
    return {
      showClose: true,
      showBackArrow: true,
      pageTitle,
    };
  }

  private setNativeBackButtonClickHandler(): void {
    console.log('Set custom shouldNativeHandleButtonPressed handler');
    this.nativeToThirdParty.shouldNativeHandleButtonPressed = (buttonPressed: string): boolean => {
      if (buttonPressed === NavigationPanelActions.BACK) {
        if (this.navigationHistoryService.isFirstPageOrHome()) {
          this.navigationHistoryService.clearAll();
          return true; // Let native handle it on marketplace page
        }
        window.history.back();
        return false; // Web handles back on other pages
      }
      this.navigationHistoryService.clearAll();
      return true;
    };
  }

  private populateWindowWithNativeToThirdPartyNamespace() {
    console.log('Add NativeToThirdPartyWVChaseJSBridgeHandler namespace');
    (window as any)[this.nativeToThirdPartyNamespaceKey] = {};
  }

  private get nativeToThirdParty() {
    return (window as any)[this.nativeToThirdPartyNamespaceKey];
  }

  externalRedirect(url: string): void {
    this.thirdPartyToNative.externalBrowser('redirect', {
      url,
    });
  }
}
